<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/main/PosMainModeTabs.vue.
  // Counts come from ordersStore.pendingOrdersByType; availability from
  // menuStore.availableOrderTypes (a tab with pending orders is never locked).
  import { computed } from 'vue';
  import { OrderType } from '@/enums/OrderEnums';
  import {
    getPosOrderTypeIcon,
    getPosOrderTypeLabel,
  } from '@/domain/pos/order-type-ui';
  import { useMenuStore } from '@/composition/use-menu';
  import { useOrdersStore } from '@/stores/orders.store';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  const props = defineProps<{
    value: OrderType;
  }>();

  const emit = defineEmits<{
    (e: 'input', mode: OrderType): void;
  }>();

  const ordersStore = useOrdersStore();
  const menuStore = useMenuStore();

  const counts = computed<Record<OrderType, number>>(() => ({
    [OrderType.Seat]:
      ordersStore.pendingOrdersByType?.[OrderType.Seat]?.length ?? 0,
    [OrderType.Away]:
      ordersStore.pendingOrdersByType?.[OrderType.Away]?.length ?? 0,
    [OrderType.Delivery]:
      ordersStore.pendingOrdersByType?.[OrderType.Delivery]?.length ?? 0,
  }));

  /** Unknown availability (stores still loading) counts as available. */
  function isTypeAvailable(type: OrderType): boolean {
    if (!menuStore.availableOrderTypes || !ordersStore.pendingOrdersByType) {
      return true;
    }
    return menuStore.availableOrderTypes.some((t) => t.type === type);
  }

  const tabs = computed(() =>
    [OrderType.Seat, OrderType.Away, OrderType.Delivery].map((type) => ({
      value: type,
      label: getPosOrderTypeLabel(type),
      icon: getPosOrderTypeIcon(type),
      count: counts.value[type],
      disabled: !isTypeAvailable(type) && counts.value[type] === 0,
    }))
  );

  function selectMode(mode: OrderType) {
    emit('input', mode);
  }
</script>

<template>
  <div class="pos-v2-mode-tabs">
    <button
      v-for="tab in tabs"
      :key="tab.value"
      type="button"
      class="tab"
      :class="{ active: props.value === tab.value }"
      :disabled="tab.disabled"
      @click="selectMode(tab.value)"
    >
      <PosUiIcon :name="tab.icon" class="tab-icon" />
      <span class="tab-label">{{ tab.label }}</span>
      <PosUiBadge
        v-if="tab.count > 0"
        :label="tab.count"
        color="neutral"
        variant="solid"
        size="xs"
        class="tab-badge"
      />
    </button>
  </div>
</template>

<style scoped lang="scss">
  // Segmented control: neutral track, active segment = raised white pill.
  .pos-v2-mode-tabs {
    @apply tw-flex tw-gap-0.5 tw-rounded-lg tw-bg-neutral-100 tw-p-0.5;

    .tab {
      @apply tw-flex tw-min-h-[40px] tw-flex-1 tw-cursor-pointer tw-select-none tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-md tw-border tw-border-transparent tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors sm:tw-min-w-[92px] sm:tw-flex-none;

      &:hover {
        @apply tw-text-neutral-800;
      }

      &:disabled {
        @apply tw-cursor-not-allowed tw-opacity-40;

        &:hover {
          @apply tw-text-neutral-500;
        }
      }

      &.active {
        @apply tw-border-neutral-200 tw-bg-white tw-font-semibold tw-text-neutral-900 tw-shadow-sm;
      }

      .tab-icon {
        @apply tw-flex-none tw-text-base;
      }

      .tab-badge {
        @apply tw-ml-0.5;
      }
    }
  }
</style>
